<template>
    <div class="comment-item" :class="{ 'nested': depth > 0 }">
        <div class="comment-main">
            <el-avatar :size="depth === 0 ? 32 : 24" :src="comment.author.avatar || defaultAvatar"
                @click="goToUserSpace(comment.author.id)" style="cursor: pointer;" />
            <div class="comment-content-wrapper">
                <div class="comment-header">
                    <span class="author-name" @click="goToUserSpace(comment.author.id)" style="cursor: pointer;">{{
                        comment.author.username }}</span>
                    <span class="comment-date">{{ formatDate(comment.createdAt) }}</span>
                    <span class="ip-location" v-if="comment.ipLocation">IP属地: {{ comment.ipLocation }}</span>
                </div>
                <div class="comment-text">
                    <v-md-editor :model-value="comment.content" mode="preview" />
                </div>
                <div class="comment-actions">
                    <el-button link type="primary" size="small" @click="handleReply">回复</el-button>
                    <el-button v-if="isAuthor" link type="primary" size="small" @click="handleEdit">编辑</el-button>
                    <el-tag v-if="comment.isAnswer" size="small" type="success" effect="light">最佳回答</el-tag>
                </div>

                <!-- Reply form inside the comment -->
                <div v-if="showReplyForm" class="reply-form">
                    <el-input v-model="replyText" type="textarea" :rows="2" placeholder="写下你的回复..." />
                    <div class="reply-actions">
                        <el-button size="small" @click="showReplyForm = false">取消</el-button>
                        <el-button size="small" type="primary" :loading="submitting" @click="submitReply">提交</el-button>
                    </div>
                </div>

                <!-- Edit form inside the comment -->
                <div v-if="showEditForm" class="reply-form">
                    <el-input v-model="editText" type="textarea" :rows="2" placeholder="修改评论内容..." />
                    <div class="reply-actions">
                        <el-button size="small" @click="showEditForm = false">取消</el-button>
                        <el-button size="small" type="primary" :loading="submitting" @click="submitEdit">保存</el-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recursive replies -->
        <div v-if="replies && replies.length > 0" class="replies-container">
            <comment-item v-for="reply in replies" :key="reply.id" :comment="reply" :replies="reply.replies"
                :depth="depth + 1" :type="type" :targetId="targetId" @reply-submitted="$emit('reply-submitted')" />
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import api, { getUser } from '@/utils/api';
import { ElMessage } from 'element-plus';
import { computed } from 'vue';

const props = defineProps({
    comment: Object,
    replies: Array,
    depth: {
        type: Number,
        default: 0
    },
    type: {
        type: String,
        default: 'forum' // 'forum' or 'checkin'
    },
    targetId: {
        type: [Number, String],
        required: true
    }
});

const emit = defineEmits(['reply-submitted']);
const router = useRouter();

const showReplyForm = ref(false);
const showEditForm = ref(false);
const replyText = ref('');
const editText = ref('');
const submitting = ref(false);
const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';

const goToUserSpace = (id) => {
    if (!id) return;
    router.push(`/user/${id}`);
};

const formatDate = (date) => {
    if (typeof date === 'number') {
        return dayjs(date * 1000).format('YYYY-MM-DD HH:mm');
    }
    return dayjs(date).format('YYYY-MM-DD HH:mm');
};

const handleReply = () => {
    showReplyForm.value = !showReplyForm.value;
    showEditForm.value = false;
};

const handleEdit = () => {
    editText.value = props.comment.content;
    showEditForm.value = !showEditForm.value;
    showReplyForm.value = false;
};

const isAuthor = computed(() => {
    const user = getUser();
    return user && (user.id === props.comment.authorId || user.id === props.comment.userId);
});

const submitReply = async () => {
    if (!replyText.value.trim()) return;
    submitting.value = true;
    try {
        const url = props.type === 'forum'
            ? `/api/forum/posts/${props.targetId}/comments`
            : `/api/checkins/${props.targetId}/comments`;

        await api.post(url, {
            content: replyText.value,
            parentId: props.comment.id
        });
        ElMessage.success('回复成功');
        replyText.value = '';
        showReplyForm.value = false;
        emit('reply-submitted');
    } catch (error) {
        ElMessage.error('回复失败');
    } finally {
        submitting.value = false;
    }
};

const submitEdit = async () => {
    if (!editText.value.trim()) return;
    submitting.value = true;
    try {
        const url = props.type === 'forum'
            ? `/api/forum/comments/${props.comment.id}`
            : `/api/checkins/comments/${props.comment.id}`;

        await api.put(url, {
            content: editText.value
        });
        ElMessage.success('已更新');
        showEditForm.value = false;
        emit('reply-submitted');
    } catch (error) {
        ElMessage.error(error.response?.data?.error || '更新失败');
    } finally {
        submitting.value = false;
    }
};
</script>

<style scoped>
.comment-item {
    margin-top: 10px;
}

.comment-main {
    display: flex;
    gap: 12px;
}

.comment-content-wrapper {
    flex: 1;
}

.comment-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 2px;
}

.author-name {
    font-weight: 500;
    font-size: 14px;
    color: #303133;
}

.comment-date {
    font-size: 12px;
    color: #909399;
}

.ip-location {
    font-size: 12px;
    color: #909399;
}

.comment-text {
    font-size: 14px;
    color: #606266;
    line-height: 1.4;
}

:deep(.v-md-editor-preview) {
    padding: 0 !important;
    margin: 0 !important;
}

:deep(.v-md-editor-preview > div) {
    margin: 0 !important;
    padding: 0 !important;
}

:deep(.github-markdown-body),
:deep(.vuepress-markdown-body) {
    padding: 0 !important;
    margin: 0 !important;
    color: inherit;
    line-height: 1.2;
}

:deep(.github-markdown-body p),
:deep(.vuepress-markdown-body p) {
    margin: 0 !important;
    line-height: inherit;
}

.comment-actions {
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.replies-container {
    margin-left: 44px;
    border-left: 2px solid #f2f6fc;
    padding-left: 10px;
}

.reply-form {
    margin-top: 10px;
    background: #f8f9fa;
    padding: 10px;
    border-radius: 4px;
}

.reply-actions {
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.nested {
    margin-top: 10px;
}
</style>
